import cn from 'clsx'
import React from 'react'
import { useDispatch } from 'hooks/use-redux'
import type { PrePopulatedSubscriptionData } from 'types/business'
import type { AccountPlan } from 'types/business2020'
import type { Summary } from 'types/summary'
import type { PricingFormState } from '@disco/state-management/reducers/forms/pricing'
import { UNLIMITED_WATERMARKING_QUOTA } from '@disco/utils/getWatermarkingQuota'
import { formatCurrency, UNLIMITED_TRACKS_QUOTA } from '@disco/utils/pricing'
import Button from 'components/ui/button'
import Alert from 'components/ui/alert'
import CustomDropdown from 'components/ui/dropdowns/custom-dropdown'
import Icon from 'components/ui/icon'
import Spinner from 'components/ui/spinner'
import ChargebeeComponent from '../chargebee-component'
import Discounts from './../discounts'
type Props = {
  period: string
  updatePricingForm: (...args: Array<any>) => any
  loadedSummary: boolean
  plan: AccountPlan
  usersQuantity: number
  tracksQuantity: number | null | undefined
  summary: Summary & {
    loading: boolean
    loaded: boolean
    error: null | { error_code: string; error_msg: string }
    coupon_code: string | null | undefined
  }
  pricingForm: PricingFormState
  handleRemoveCoupon: (...args: Array<any>) => any
  total_in_cents: number
  sendFormWaiting: boolean
  handleSubmit: (...args: Array<any>) => any
  isOverQuota: boolean
  paymentDetailsSaved: boolean
  isPhone: boolean
  prePopulatedSubscriptionData: PrePopulatedSubscriptionData
}

function getComponentPrice(
  summary: Summary,
  componentName: string | null,
  productHandle?: string,
) {
  const currentComponent = summary.line_items?.find(priceItem => {
    if (productHandle) {
      return priceItem.product_handle
    }

    if (componentName) {
      return priceItem.component_handle?.includes(componentName)
    }
  })

  if (currentComponent) {
    return currentComponent.amount_in_cents
  }

  return 0
}

const BillingSummary = ({
  period,
  updatePricingForm,
  loadedSummary,
  plan,
  usersQuantity,
  tracksQuantity,
  summary,
  pricingForm,
  handleRemoveCoupon,
  total_in_cents,
  sendFormWaiting,
  handleSubmit,
  isOverQuota,
  paymentDetailsSaved,
  isPhone,
  prePopulatedSubscriptionData,
}: Props): React.ReactNode | React.ReactElement<'section'> => {
  const dispatch = useDispatch()
  const open = pricingForm.openBillingSummary

  function handleSubmitOrScroll(evt) {
    if (!paymentDetailsSaved) {
      const form = document.querySelector('#chargebee-form')
      form?.scrollIntoView?.()
      return
    }

    handleSubmit(evt)
  }

  let confirmAndSubscribeTooltip: string | undefined = undefined

  if (isOverQuota) {
    confirmAndSubscribeTooltip = `You’re over the users or tracks for this plan.
    Please sign up to a plan that covers your current usage,
    or remove users and tracks.`
  }

  if (!paymentDetailsSaved) {
    confirmAndSubscribeTooltip = 'Save your payment details to continue.'
  }

  const totalContent = (
    <div className="ChargebeeForm_Total">
      {summary.error ? (
        <Alert status="danger">Error: {summary.error.error_msg}.</Alert>
      ) : (
        <>
          <span>Total:</span>
          <b>{formatCurrency(total_in_cents)}</b>
        </>
      )}
    </div>
  )
  const content = (
    <>
      <h2 className="ChargebeeForm_Title ChargebeeForm_Title--WithSubTitle">
        Billing summary
      </h2>
      <CustomDropdown
        value={period}
        onChange={option => {
          updatePricingForm({
            paymentPeriod: option.value,
          })
        }}
        options={[
          {
            label: 'Monthly billing',
            value: 'monthly',
          },
          {
            label: 'Annual billing',
            value: 'annual',
          },
        ]}
      />
      <div className="ChargebeeForm_Note">
        {period === 'monthly'
          ? 'Save 10% with annual billing'
          : 'You’re saving 10% with annual billing'}
      </div>
      {loadedSummary ? (
        <div className="ChargebeeForm_TableWrapper">
          {plan.name ? (
            <ChargebeeComponent
              title={`${plan.name && plan.name.toUpperCase()} plan`}
              subTitle={
                <>
                  {usersQuantity ? `${usersQuantity} Users ` : ''}
                  {tracksQuantity &&
                  tracksQuantity !== UNLIMITED_TRACKS_QUOTA ? (
                    <>
                      {tracksQuantity >= 1000
                        ? tracksQuantity / 1000
                        : tracksQuantity}
                      {tracksQuantity >= 1000 ? <span>K</span> : null}
                      <span> Tracks </span>
                    </>
                  ) : (
                    ''
                  )}
                  {tracksQuantity === UNLIMITED_TRACKS_QUOTA
                    ? `and unlimited tracks`
                    : ''}
                </>
              }
              price={
                summary.error
                  ? null
                  : formatCurrency(
                      getComponentPrice(summary, 'storage') +
                        getComponentPrice(summary, 'user') +
                        getComponentPrice(summary, null, 'plan'),
                    )
              }
            />
          ) : null}
          {pricingForm.componentsQuantity.discoverySuite && (
            <ChargebeeComponent
              title="Discovery Suite"
              subTitle={`${pricingForm.componentsQuantity.discoverySuite} tracks`}
              price={
                summary.error
                  ? null
                  : formatCurrency(getComponentPrice(summary, 'tagging'))
              }
              remove={() => {
                updatePricingForm({
                  componentsQuantity: {
                    ...pricingForm.componentsQuantity,
                    discoverySuite: null,
                  },
                })
              }}
              isPhone={isPhone}
            />
          )}
          {pricingForm.componentsQuantity.watermarking && (
            <ChargebeeComponent
              title="Watermarking"
              subTitle={`${
                pricingForm.componentsQuantity.watermarking ===
                UNLIMITED_WATERMARKING_QUOTA
                  ? 'Unlimited'
                  : pricingForm.componentsQuantity.watermarking
              } Watermarked playlists`}
              price={
                summary.error
                  ? null
                  : formatCurrency(getComponentPrice(summary, 'watermark'))
              }
              remove={() => {
                updatePricingForm({
                  componentsQuantity: {
                    ...pricingForm.componentsQuantity,
                    watermarking: null,
                  },
                })
              }}
              isPhone={isPhone}
            />
          )}

          <Discounts
            summary={summary}
            onRemoveCoupon={
              !prePopulatedSubscriptionData.coupon_code
                ? handleRemoveCoupon
                : undefined
            }
          />

          {totalContent}
          <div className="ChargebeeForm_ConfirmButton">
            <Button
              appearance="primary"
              caption="Confirm and subscribe"
              progressIndicator={sendFormWaiting}
              progressCaption="Loading"
              onClick={handleSubmitOrScroll}
              disabled={isOverQuota}
              tooltip={confirmAndSubscribeTooltip}
              fullWidth={isPhone}
              textCenter={isPhone}
            />
          </div>
        </div>
      ) : (
        <Spinner />
      )}
    </>
  )

  if (isPhone) {
    return (
      <>
        <div
          className={cn('ChargebeeForm_SummaryButton', {
            'ChargebeeForm_SummaryButton--Open': open,
          })}
        >
          <div
            className={cn('ChargebeeForm_SummaryButtonArrow', {
              'ChargebeeForm_SummaryButtonArrow--Open': open,
            })}
            onClick={() =>
              dispatch(
                updatePricingForm({
                  openBillingSummary: !open,
                }),
              )
            }
          >
            <Icon type="arrow-right-big-v2" width="16" height="16" />
          </div>
          {!open && (
            <div className="ChargebeeForm_SummaryButtonContainer">
              {totalContent}
            </div>
          )}
          <div
            className={cn('ChargebeeForm_Wrapper--Summary', {
              'ChargebeeForm_Wrapper--Mobile-Hidden': !open,
            })}
          >
            {content}
          </div>
        </div>
      </>
    )
  }

  return (
    <section className="ChargebeeForm_Wrapper ChargebeeForm_Wrapper--Summary">
      {content}
    </section>
  )
}

export default BillingSummary
